import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Application } from '@feathersjs/feathers';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { District, DistrictSource } from '../../app/feathers/districts/District';
import { DistrictVotingType } from '../../app/feathers/districts/DistrictVotingType';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import { NamingPattern } from '../../app/feathers/naming-patterns/NamingPattern';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { loadPaginatedFrontend, useFeathers } from '../../app/util';
import {
  identifierPayload,
  validateDistrictIdentifiers,
} from '../Tier3EditDistrictModal/district-identifiers';
import { Modal } from '../Modal';
import {
  AtLargeSuggestion,
  CreateDistrictSuggestion,
  DistrictRow,
  childrenStructureForCreation,
  districtNamePreview,
  districtRowsForSuggestion,
  identifierTypeForValue,
  orderNamingPatterns,
  parentMetadataForCreation,
  siblingDistrictQuery,
  suggestAtLargeStatus,
  tierForDistrictType,
} from './create-district-modal-helpers';

interface CreateDistrictModalProps {
  suggestion: CreateDistrictSuggestion;
  selectableTypes?: string[];
  stateConfiguration?: StateConfiguration | null;
  suggestedGeometry?: unknown;
  loadSuggestedGeometry?: () => Promise<unknown | undefined>;
  suggestedParentDistrict?: District;
  loadSuggestedParentDistrict?: () => Promise<District | undefined>;
  creationSources?: DistrictSource[];
  creationNotes?: string[];
  onCreate: (district: District) => void;
  onClose: () => void;
}

const CreateDistrictModal: React.FC<CreateDistrictModalProps> = ({
  suggestion,
  selectableTypes,
  stateConfiguration,
  suggestedGeometry,
  loadSuggestedGeometry,
  suggestedParentDistrict,
  loadSuggestedParentDistrict,
  creationSources,
  creationNotes,
  onCreate,
  onClose,
}) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [selectedType, setSelectedType] = useState(
    suggestion.type || (selectableTypes?.length === 1 ? selectableTypes[0] : ''),
  );
  const effectiveSuggestion = useMemo(
    () => ({ ...suggestion, type: selectedType || undefined }),
    [selectedType, suggestion],
  );
  const [rows, setRows] = useState<DistrictRow[]>(
    () => districtRowsForSuggestion([], effectiveSuggestion),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [atLargeLoading, setAtLargeLoading] = useState(false);
  const [atLargeSuggestion, setAtLargeSuggestion] = useState<AtLargeSuggestion | null>(null);
  const [siblingsLoaded, setSiblingsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [namingPatterns, setNamingPatterns] = useState<NamingPattern[]>([]);
  const [namingPatternsLoading, setNamingPatternsLoading] = useState(
    Boolean(effectiveSuggestion.parentId && effectiveSuggestion.type),
  );
  const [namingPatternError, setNamingPatternError] = useState<string | null>(null);
  const [previewParentName, setPreviewParentName] = useState(effectiveSuggestion.parentDistrict);

  const tier = tierForDistrictType(effectiveSuggestion.type, stateConfiguration);
  const query = useMemo(
    () => siblingDistrictQuery(effectiveSuggestion, stateConfiguration),
    [effectiveSuggestion, stateConfiguration],
  );

  const resolveParentDistrict = useCallback(async (): Promise<District | undefined> => {
    if (!effectiveSuggestion.parentId) return undefined;
    return suggestedParentDistrict
      ?? await loadSuggestedParentDistrict?.()
      ?? await feathers.getService('districts').get(effectiveSuggestion.parentId);
  }, [
    effectiveSuggestion.parentId,
    feathers,
    loadSuggestedParentDistrict,
    suggestedParentDistrict,
  ]);

  useEffect(() => {
    let cancelled = false;

    setRows(districtRowsForSuggestion([], effectiveSuggestion));
    setError(null);
    setSiblingsLoaded(tier !== 3);

    if (!query) {
      if (tier === 3 && (!effectiveSuggestion.parentId || !effectiveSuggestion.type)) {
        setError('Sibling districts could not be loaded because the parent or district type is missing.');
      }
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    loadPaginatedFrontend<District>('districts', query, feathers, 50)
      .then(siblings => {
        if (cancelled) return;
        setRows(districtRowsForSuggestion(siblings, effectiveSuggestion));
        setSiblingsLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Sibling districts could not be loaded. The suggested district is still shown below.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [effectiveSuggestion, feathers, query, tier]);

  useEffect(() => {
    let cancelled = false;

    const loadNamingPatterns = async () => {
      setNamingPatterns([]);
      setNamingPatternError(null);
      setPreviewParentName(effectiveSuggestion.parentDistrict);

      if (!effectiveSuggestion.parentId || !effectiveSuggestion.type) {
        setNamingPatternsLoading(false);
        return;
      }

      setNamingPatternsLoading(true);
      try {
        const parentDistrict = await resolveParentDistrict();
        if (!parentDistrict) {
          throw new Error('The parent district could not be loaded.');
        }

        const namingPatternService = feathers.getService('naming-patterns');
        const [parentPatternResult, nationalPatternResult] = await Promise.all([
          parentDistrict.namingPatternId
            ? namingPatternService.find({ query: { _id: parentDistrict.namingPatternId } })
            : Promise.resolve(null),
          namingPatternService.find({ query: { key: 'default-national' } }),
        ]);
        if (cancelled) return;

        setPreviewParentName(parentDistrict.name || effectiveSuggestion.parentDistrict);
        setNamingPatterns(orderNamingPatterns(
          parentPatternResult?.data?.[0],
          nationalPatternResult.data?.[0],
        ));
      } catch {
        if (!cancelled) {
          setNamingPatternError('Naming patterns could not be loaded, so district names cannot be previewed.');
        }
      } finally {
        if (!cancelled) setNamingPatternsLoading(false);
      }
    };

    loadNamingPatterns();
    return () => {
      cancelled = true;
    };
  }, [
    feathers,
    effectiveSuggestion.parentDistrict,
    effectiveSuggestion.parentId,
    effectiveSuggestion.type,
    resolveParentDistrict,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadAtLargeSuggestion = async () => {
      if (tier !== 3) {
        setAtLargeLoading(false);
        setAtLargeSuggestion(null);
        return;
      }

      setAtLargeLoading(true);
      setAtLargeSuggestion(null);
      try {
        if (!effectiveSuggestion.parentId) {
          setAtLargeSuggestion({
            suggestedAtLarge: false,
            message: 'No at-large suggestion: the parent district is unavailable.',
          });
          return;
        }

        const suppliedParentDistrict = suggestedParentDistrict
          ?? await loadSuggestedParentDistrict?.();
        const parentDistrictResult = suppliedParentDistrict?.geoDistrict?.boundary
          || suppliedParentDistrict?.hasGeodistrict === false
          ? null
          : await feathers.getService('districts').find({
            query: {
              _id: effectiveSuggestion.parentId,
              $includeGeoDistrict: true,
              $limit: 1,
            },
          });
        const parentDistrict = parentDistrictResult?.data?.[0] ?? suppliedParentDistrict;
        if (cancelled) return;

        if (!parentDistrict) {
          setAtLargeSuggestion({
            suggestedAtLarge: false,
            message: 'No at-large suggestion: the parent district is unavailable.',
          });
          return;
        }
        if (parentDistrict.hasGeodistrict === false) {
          const result = suggestAtLargeStatus(false);
          setAtLargeSuggestion(result);
          return;
        }
        if (!parentDistrict.geoDistrict?.boundary) {
          setAtLargeSuggestion({
            suggestedAtLarge: false,
            message: 'No at-large suggestion: the parent geodistrict geometry is unavailable.',
          });
          return;
        }

        const childGeometry = suggestedGeometry !== undefined
          ? suggestedGeometry
          : await loadSuggestedGeometry?.();
        if (cancelled) return;

        const result = suggestAtLargeStatus(
          true,
          parentDistrict.geoDistrict?.boundary,
          childGeometry,
        );
        setAtLargeSuggestion(result);
      } catch {
        if (!cancelled) {
          setAtLargeSuggestion({
            suggestedAtLarge: false,
            message: 'No at-large suggestion: the required geometry could not be loaded.',
          });
        }
      } finally {
        if (!cancelled) setAtLargeLoading(false);
      }
    };

    loadAtLargeSuggestion();
    return () => {
      cancelled = true;
    };
  }, [
    feathers,
    loadSuggestedGeometry,
    loadSuggestedParentDistrict,
    suggestedGeometry,
    suggestedParentDistrict,
    effectiveSuggestion.parentId,
    tier,
  ]);

  useEffect(() => {
    if (tier !== 3 || !atLargeSuggestion || atLargeLoading || loading) return;
    setRows(currentRows => currentRows.map(row => (
      row.existingDistrictId
        ? row
        : {
          ...row,
          votingType: atLargeSuggestion.suggestedAtLarge
            ? 'representative-district'
            : 'voting-district',
        }
    )));
  }, [atLargeLoading, atLargeSuggestion, loading, tier]);

  const suggestedRowIndex = rows.findIndex(row => !row.existingDistrictId);
  const identifierType = identifierTypeForValue(rows[suggestedRowIndex]?.identifier);
  const validation = useMemo(
    () => validateDistrictIdentifiers(rows, identifierType),
    [identifierType, rows],
  );
  const rowPreviews = useMemo(
    () => rows.map(row => districtNamePreview(
      row,
      identifierType,
      {
        type: effectiveSuggestion.type,
        parentDistrict: previewParentName,
      },
      namingPatterns,
    )),
    [
      identifierType,
      namingPatterns,
      previewParentName,
      rows,
      effectiveSuggestion.type,
    ],
  );
  const namingPatternMissing = (
    !namingPatternsLoading
    && !namingPatternError
    && Boolean(effectiveSuggestion.parentId && effectiveSuggestion.type)
    && suggestedRowIndex !== -1
    && validation.normalizedValues[suggestedRowIndex] !== null
    && !rowPreviews[suggestedRowIndex]
  );
  const prettyType = humanizeDistrictType(
    effectiveSuggestion.type || 'district',
    stateConfiguration?.typeConfigurations || null,
  );

  const createDistrict = async () => {
    if (
      saving
      || !validation.isValid
      || !effectiveSuggestion.type
      || !effectiveSuggestion.parentId
      || (tier !== 2 && tier !== 3)
      || (tier === 3 && (!siblingsLoaded || atLargeLoading))
    ) {
      return;
    }

    if (suggestedRowIndex === -1) return;

    setSaving(true);
    setSaveError(null);
    let parentBeforePatch: District | undefined;
    let parentWasPatched = false;
    let parentMetadataWasPatched = false;

    try {
      const existingRows = rows
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => Boolean(row.existingDistrictId));
      await Promise.all(existingRows.map(({ row, index }) => (
        feathers.getService('districts').patch(row.existingDistrictId!, {
          votingType: row.votingType,
          ...identifierPayload(identifierType, validation.normalizedValues[index]),
        })
      )));

      if (tier === 3) {
        parentBeforePatch = await resolveParentDistrict();
        if (!parentBeforePatch) {
          throw new Error('The parent district could not be loaded.');
        }
        parentMetadataWasPatched = Boolean(creationSources?.length || creationNotes?.length);
        const parentMetadata = parentMetadataForCreation(
          parentBeforePatch,
          effectiveSuggestion.type,
          creationSources,
          creationNotes,
        );
        await feathers.getService('districts').patch(parentBeforePatch._id, {
          childrenStructure: childrenStructureForCreation(
            parentBeforePatch,
            effectiveSuggestion.type,
            rows.length,
          ),
          ...(parentMetadataWasPatched ? parentMetadata : {}),
        });
        parentWasPatched = true;
      }

      const suggestedRow = rows[suggestedRowIndex];
      const createdDistrict = await feathers.getService('districts').create({
        type: effectiveSuggestion.type,
        parentId: effectiveSuggestion.parentId,
        votingType: suggestedRow.votingType,
        ...identifierPayload(
          identifierType,
          validation.normalizedValues[suggestedRowIndex],
        ),
        ...(creationSources?.length ? { sources: creationSources } : {}),
        ...(creationNotes?.length ? { notes: creationNotes } : {}),
        $refreshSiblingPopulations: true,
      });
      onCreate(createdDistrict);
    } catch (creationError) {
      if (parentWasPatched && parentBeforePatch) {
        try {
          await feathers.getService('districts').patch(parentBeforePatch._id, {
            childrenStructure: parentBeforePatch.childrenStructure || {},
            ...(parentMetadataWasPatched ? {
              notes: parentBeforePatch.notes || [],
              sources: parentBeforePatch.sources || [],
            } : {}),
          });
        } catch {
          setSaveError('District creation failed, and the parent children structure could not be restored.');
          setSaving(false);
          return;
        }
      }
      setSaveError(
        creationError instanceof Error
          ? creationError.message
          : 'The district could not be created.',
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDisabled = (
    saving
    || loading
    || !validation.isValid
    || !effectiveSuggestion.type
    || !effectiveSuggestion.parentId
    || (tier !== 2 && tier !== 3)
    || (tier === 3 && (!siblingsLoaded || atLargeLoading))
  );

  return (
    <Modal onClose={saving ? () => undefined : onClose} style={{ minWidth: '680px', padding: '32px' }}>
      <Typography variant="h5">
        {selectableTypes && !selectedType ? 'Create child district' : `Create ${prettyType} district`}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
        Review the district information below before creating it.
      </Typography>
      {selectableTypes && (
        <FormControl fullWidth size="small" sx={{ mt: 2, maxWidth: '300px' }}>
          <InputLabel id="create-district-type-label">District type</InputLabel>
          <Select
            labelId="create-district-type-label"
            label="District type"
            value={selectedType}
            disabled={saving}
            onChange={event => setSelectedType(event.target.value)}
          >
            {selectableTypes.map(type => (
              <MenuItem key={type} value={type}>
                {humanizeDistrictType(type, stateConfiguration?.typeConfigurations || null)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
      {namingPatternError && (
        <Alert severity="warning" sx={{ mt: 1 }}>{namingPatternError}</Alert>
      )}
      {namingPatternMissing && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          No naming pattern is configured for the {identifierType} identifier type.
          The new district name cannot be previewed.
        </Alert>
      )}
      {saveError && <Alert severity="error" sx={{ mt: 1 }}>{saveError}</Alert>}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading sibling districts…</Typography>
        </div>
      )}

      {effectiveSuggestion.type && (
        <TableContainer component={Paper} sx={{ mt: 2, border: 'solid 1px #dddddd' }}>
        <Table size="small" aria-label="Sibling districts">
          <TableHead>
            <TableRow>
              <TableCell>Identifier</TableCell>
              <TableCell>Preview</TableCell>
              {tier === 3 && <TableCell align="center">At-large?</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const unchanged = row.identifier === row.originalIdentifier;
              const preview = (unchanged && row.longName) || rowPreviews[index];

              return (
                <TableRow
                  key={row.existingDistrictId || 'suggested-district'}
                  sx={{ backgroundColor: row.existingDistrictId ? undefined : '#fff8e1' }}
                >
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      value={row.identifier ?? ''}
                      disabled={saving}
                      onChange={event => {
                        const value = identifierTypeForValue(event.target.value) === 'letter'
                          ? event.target.value.toUpperCase()
                          : event.target.value;
                        setRows(currentRows => currentRows.map((currentRow, rowIndex) => (
                          rowIndex === index ? { ...currentRow, identifier: value } : currentRow
                        )));
                      }}
                      error={Boolean(validation.rowErrors[index])}
                      helperText={validation.rowErrors[index] || undefined}
                    />
                  </TableCell>
                  <TableCell>
                    {namingPatternsLoading && !(unchanged && row.longName) ? (
                      <Skeleton variant="text" width={180} />
                    ) : (
                      <Typography variant="body2">
                        {preview || 'Preview unavailable'}
                      </Typography>
                    )}
                  </TableCell>
                  {tier === 3 && (
                    <TableCell align="center">
                      <Checkbox
                        checked={row.votingType === 'representative-district'}
                        size="small"
                        disabled={saving || (!row.existingDistrictId && atLargeLoading)}
                        onChange={event => {
                          const votingType: DistrictVotingType = event.target.checked
                            ? 'representative-district'
                            : 'voting-district';
                          setRows(currentRows => currentRows.map((currentRow, rowIndex) => (
                            rowIndex === index ? { ...currentRow, votingType } : currentRow
                          )));
                        }}
                      />
                      {!row.existingDistrictId && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ display: 'block', maxWidth: 260, textAlign: 'left' }}
                        >
                          {atLargeLoading
                            ? 'Calculating at-large suggestion…'
                            : atLargeSuggestion?.message}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </TableContainer>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={createDistrict} disabled={confirmDisabled}>
          {saving ? 'Creating…' : 'Confirm'}
        </Button>
      </div>
    </Modal>
  );
};

export { CreateDistrictModal };
